import { Box, Typography } from '@mui/material'

/**
 * Typographic primitives for the docs body.
 *
 * These exist so every page sets measure, rhythm and colour identically. The
 * house style from the landing page carries over: hairline rules instead of
 * cards, sentence case, and the accent colour reserved for actions.
 */

/** Comfortable reading measure. Long prose lines are the main legibility bug. */
export const MEASURE = 68

export function Lede({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{
      fontSize: '1.075rem', lineHeight: 1.65, color: 'var(--text-secondary)',
      maxWidth: `${MEASURE}ch`, mb: 1,
    }}>
      {children}
    </Typography>
  )
}

export function P({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{
      lineHeight: 1.75, color: 'var(--text-secondary)',
      maxWidth: `${MEASURE}ch`, mb: 2.25,
    }}>
      {children}
    </Typography>
  )
}

/**
 * A linkable H2. The id is what makes a concept deep-linkable, so it is
 * required rather than derived from the text — derived slugs change silently
 * when someone rewords a heading, and every external link breaks.
 */
export function Section({ id, title, children }: {
  id: string
  title: string
  children: React.ReactNode
}) {
  return (
    <Box component="section" sx={{ mt: 6 }}>
      <Typography
        component="h2"
        id={id}
        sx={{
          // Clears the sticky nav when jumped to from the table of contents.
          scrollMarginTop: '96px',
          fontSize: '1.3rem', fontWeight: 650, letterSpacing: '-0.02em',
          mb: 2,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  )
}

export function H3({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="h3" sx={{
      fontSize: '1.02rem', fontWeight: 650, letterSpacing: '-0.01em', mt: 3.5, mb: 1.25,
    }}>
      {children}
    </Typography>
  )
}

/** Term / definition rows on hairlines. Used wherever a page enumerates states. */
export function DefList({ items }: { items: Array<[React.ReactNode, React.ReactNode]> }) {
  return (
    <Box sx={{ my: 3, maxWidth: `${MEASURE + 12}ch` }}>
      {items.map(([term, def], i) => (
        <Box key={i} sx={{
          display: 'flex', gap: 2.5, py: 1.5,
          borderTop: '1px solid var(--border-soft)',
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
          <Box sx={{ minWidth: 168, flexShrink: 0 }}>
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {term}
            </Typography>
          </Box>
          <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.94rem' }}>
            {def}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

/** Numbered steps, styled like the landing page's numbered features. */
export function Steps({ items }: { items: Array<{ title: string; body: React.ReactNode }> }) {
  return (
    <Box sx={{ my: 3, maxWidth: `${MEASURE + 12}ch` }}>
      {items.map((step, i) => (
        <Box key={step.title} sx={{
          display: 'flex', gap: 2.5, py: 2,
          borderTop: '1px solid var(--border-soft)',
        }}>
          <Typography className="fw-num" sx={{
            fontSize: '0.75rem', color: 'var(--text-faint)', pt: '4px', minWidth: 22,
          }}>
            {String(i + 1).padStart(2, '0')}
          </Typography>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{step.title}</Typography>
            <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {step.body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/** Inline monospace, for column names, enum values and endpoints. */
export function C({ children }: { children: React.ReactNode }) {
  return (
    <Box component="code" className="fw-num" sx={{
      fontSize: '0.86em', color: 'var(--text)',
      background: 'var(--glass-2)', border: '1px solid var(--border-soft)',
      borderRadius: '4px', px: '4px', py: '1px', whiteSpace: 'nowrap',
    }}>
      {children}
    </Box>
  )
}

export function Pre({ children }: { children: React.ReactNode }) {
  return (
    <Box component="pre" className="fw-num" sx={{
      my: 3, p: 2, overflowX: 'auto',
      fontSize: '0.82rem', lineHeight: 1.7, color: 'var(--text-secondary)',
      background: 'var(--glass-1)', border: '1px solid var(--border-soft)',
      borderRadius: 'var(--radius-sm)',
    }}>
      {children}
    </Box>
  )
}

/**
 * The editorial spine of these docs: every concept ends by pointing at the
 * feature that removes the manual work. Marked with a left rule rather than a
 * tinted card so it reads as an aside, not an advert.
 */
export function Automate({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{
      my: 4, pl: 2.5, py: 0.5,
      borderLeft: '2px solid var(--inflow)',
      maxWidth: `${MEASURE}ch`,
    }}>
      <Typography sx={{
        fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--text-faint)', mb: 1,
      }}>
        You should not be doing this by hand
      </Typography>
      <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        {children}
      </Typography>
    </Box>
  )
}
